/**
 * RspStore.js — MobX Store pour le Phidget Résolution de Problèmes (RSP)
 * Design System SEIZO
 */
import { makeAutoObservable } from 'mobx';
import axios from 'axios';

export default class RspStore {
    // ── State ─────────────────────────────────────────────────────────────────
    view = 'liste';
    showFormCreation = false; // 'liste' | 'etape1' | 'etape2' | 'etape3' | 'etape4' | 'etape5'
    problemes = [];
    problemeActif = null;
    isLoading = false;
    _token = null;
    _idWidget = null;

    constructor() {
        makeAutoObservable(this);
    }

    // ── Init ──────────────────────────────────────────────────────────────────
    initialize(token, idWidget) {
        // Si c'est le même widget déjà initialisé, on ne réinitialise pas l'état
        // (évite de perdre le problemeActif et la vue courante lors de la réouverture)
        if (this._idWidget === idWidget && this._token === token) {
            return;
        }
        this._token = token;
        this._idWidget = idWidget;
        this.view = 'liste';
        this.problemeActif = null;
    }

    // ── Navigation ────────────────────────────────────────────────────────────
    setView(view) {
        this.view = view;
    }

    goToListe() {
        this.view = 'liste';
        this.problemeActif = null;
        this.showFormCreation = false;
    }
    setShowFormCreation(v) { this.showFormCreation = v; }
    ouvrirFormCreation() {
        this.view = 'liste';
        this.problemeActif = null;
        this.showFormCreation = true;
    }

    goToEtape(n) {
        this.view = `etape${n}`;
    }

    advanceEtape(n) {
        if (this.problemeActif) {
            const etapeCourante = Math.max(this.problemeActif.etape_courante || 1, n);
            this.problemeActif = { ...this.problemeActif, etape_courante: etapeCourante };
        }
        this.view = `etape${n}`;
    }

    // ── Headers ───────────────────────────────────────────────────────────────
    get _headers() {
        return { Authorization: `Bearer ${this._token}` };
    }

    get _intertotemActorHeaders() {
        const userToken = typeof document === 'undefined'
            ? null
            : document.cookie
                .split(';')
                .map(cookie => cookie.trim())
                .find(cookie => cookie.startsWith('user_apiToken='))
                ?.slice('user_apiToken='.length);

        if (!userToken) {
            throw new Error('Votre session utilisateur a expiré. Reconnectez-vous pour agir sur une action inter-Totems.');
        }

        return { Authorization: `Bearer ${userToken}` };
    }

    get _baseUrl() {
        return process.env.REACT_APP_API_URL + '/api/RSP';
    }

    // ── Fetch problèmes ───────────────────────────────────────────────────────
    async fetchProblemes() {
        if (!this._idWidget) return;
        this.isLoading = true;
        try {
            const res = await axios.get(`${this._baseUrl}/widget/${this._idWidget}/problemes`, {
                headers: this._headers,
            });
            this.problemes = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.problemes || []);
        } catch (e) {
            console.error('RspStore.fetchProblemes', e);
        } finally {
            this.isLoading = false;
        }
    }

    // ── Fetch un problème avec causes + solutions ─────────────────────────────
    async fetchProbleme(id) {
        try {
            const res = await axios.get(`${this._baseUrl}/problemes/${id}`, {
                headers: this._headers,
            });
            this.problemeActif = res.data?.data || res.data;
            // Mettre à jour dans la liste aussi
            const idx = this.problemes.findIndex(p => p.id === id);
            if (idx >= 0) this.problemes[idx] = res.data?.data || res.data;
        } catch (e) {
            console.error('RspStore.fetchProbleme', e);
        }
    }

    // ── Créer un problème ─────────────────────────────────────────────────────
    async createProbleme(data) {
        try {
            const res = await axios.post(`${this._baseUrl}/widget/${this._idWidget}/problemes`, data, {
                headers: this._headers,
            });
            const nouveau = res.data?.data || res.data;
            this.problemes = [nouveau, ...this.problemes];
            this.problemeActif = nouveau;
            this.view = 'etape1';
            return nouveau;
        } catch (e) {
            console.error('RspStore.createProbleme', e);
        }
    }

    // ── Mettre à jour un problème ─────────────────────────────────────────────
    async updateProbleme(id, data) {
        try {
            const res = await axios.put(`${this._baseUrl}/problemes/${id}`, data, {
                headers: this._headers,
            });
            const updated = res.data?.data || res.data;
            const idx = this.problemes.findIndex(p => p.id === id);
            if (idx >= 0) this.problemes[idx] = updated;
            if (this.problemeActif?.id === id) {
                this.problemeActif = { ...this.problemeActif, ...updated };
            }
            return updated;
        } catch (e) {
            console.error('RspStore.updateProbleme', e);
        }
    }

    // ── Supprimer un problème ─────────────────────────────────────────────────
    async deleteProbleme(id) {
        try {
            await axios.delete(`${this._baseUrl}/problemes/${id}`, { headers: this._headers });
            this.problemes = this.problemes.filter(p => p.id !== id);
            if (this.problemeActif?.id === id) this.goToListe();
        } catch (e) {
            console.error('RspStore.deleteProbleme', e);
        }
    }
    // ── Marquer résolu ────────────────────────────────────────────────────────
    // ── Sélectionner un problème et aller à l'étape 1 ─────────────────────────
    async selectProbleme(id) {
        await this.fetchProbleme(id);
        this.view = `etape${this.problemeActif?.etape_courante || 1}`;
    }

    async marquerResolu() {
        if (!this.problemeActif) return;
        const updated = await this.updateProbleme(this.problemeActif.id, { statut: 'resolu' });
        if (updated) {
            setTimeout(() => this.goToListe(), 1200);
        }
    }

    // ── Causes ───────────────────────────────────────────────────────────────
    async getCauseIndicatorTargets(problemeId) {
        try {
            const res = await axios.get(`${this._baseUrl}/problemes/${problemeId}/indicator-targets`, {
                headers: this._headers,
            });
            return { targets: res.data?.data || [], error: null };
        } catch (e) {
            console.error('RspStore.getCauseIndicatorTargets', e);
            return {
                targets: [],
                error: e?.response?.data?.message || 'Les indicateurs de ce Totem sont indisponibles.',
            };
        }
    }

    async addCause(problemeId, data) {
        try {
            const res = await axios.post(`${this._baseUrl}/problemes/${problemeId}/causes`, data, {
                headers: this._headers,
            });
            const cause = res.data?.data || res.data;
            if (this.problemeActif?.id === problemeId) {
                this.problemeActif = {
                    ...this.problemeActif,
                    causes: [...(this.problemeActif.causes || []), cause],
                };
            }
            return cause;
        } catch (e) {
            console.error('RspStore.addCause', e);
        }
    }

    async updateCause(causeId, data) {
        try {
            const res = await axios.put(`${this._baseUrl}/causes/${causeId}`, data, {
                headers: this._headers,
            });
            const updated = res.data?.data || res.data;
            if (this.problemeActif) {
                this.problemeActif = {
                    ...this.problemeActif,
                    causes: this.problemeActif.causes.map(c => c.id === causeId ? updated : c),
                };
            }
        } catch (e) {
            console.error('RspStore.updateCause', e);
        }
    }

    async deleteCause(causeId) {
        try {
            await axios.delete(`${this._baseUrl}/causes/${causeId}`, { headers: this._headers });
            if (this.problemeActif) {
                this.problemeActif = {
                    ...this.problemeActif,
                    causes: this.problemeActif.causes.filter(c => c.id !== causeId),
                };
            }
        } catch (e) {
            console.error('RspStore.deleteCause', e);
        }
    }

    // ── Solutions ─────────────────────────────────────────────────────────────
    async addSolution(problemeId, data) {
        try {
            const res = await axios.post(`${this._baseUrl}/problemes/${problemeId}/solutions`, data, {
                headers: this._headers,
            });
            const sol = res.data?.data || res.data;
            if (this.problemeActif?.id === problemeId) {
                this.problemeActif = {
                    ...this.problemeActif,
                    solutions: [...(this.problemeActif.solutions || []), sol],
                };
            }
            return sol;
        } catch (e) {
            console.error('RspStore.addSolution', e);
        }
    }

    async updateSolution(solutionId, data) {
        try {
            const res = await axios.put(`${this._baseUrl}/solutions/${solutionId}`, data, {
                headers: this._headers,
            });
            const updated = res.data?.data || res.data;
            if (this.problemeActif) {
                this.problemeActif = {
                    ...this.problemeActif,
                    solutions: this.problemeActif.solutions.map(s => s.id === solutionId ? updated : s),
                };
            }
        } catch (e) {
            console.error('RspStore.updateSolution', e);
        }
    }

    async deleteSolution(solutionId) {
        try {
            await axios.delete(`${this._baseUrl}/solutions/${solutionId}`, { headers: this._headers });
            if (this.problemeActif) {
                this.problemeActif = {
                    ...this.problemeActif,
                    solutions: this.problemeActif.solutions.filter(s => s.id !== solutionId),
                };
            }
        } catch (e) {
            console.error('RspStore.deleteSolution', e);
        }
    }
    async creerActionDepuisSolution(solutionId, data) {
        try {
            const res = await axios.post(`${this._baseUrl}/solutions/${solutionId}/creer-action`, data, {
                headers: {
                    ...this._intertotemActorHeaders,
                    'Idempotency-Key': this._idempotencyKey('rsp-send'),
                },
            });

            const updatedSolution = res.data?.solution;
            if (updatedSolution && this.problemeActif) {
                this.problemeActif = {
                    ...this.problemeActif,
                    solutions: this.problemeActif.solutions.map(solution =>
                        solution.id === solutionId ? updatedSolution : solution
                    ),
                };
            }

            return res.data?.data || res.data;
        } catch (e) {
            console.error('RspStore.creerActionDepuisSolution', e);
            throw e;
        }
    }

    _idempotencyKey(prefix) {
        const unique = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return `${prefix}-${unique}`;
    }

    async getIntertotemTargets(solutionId) {
        // Le backend dérive le Totem source du widget RSP et ne renvoie que les
        // destinataires accessibles du même Workspace.
        try {
            const res = await axios.get(`${this._baseUrl}/solutions/${solutionId}/intertotem-targets`, {
                headers: this._intertotemActorHeaders,
            });
            return { targets: res.data?.data || [], error: null };
        } catch (e) {
            console.error('RspStore.getIntertotemTargets', e);
            return {
                targets: [],
                error: e.response?.data?.message || 'Impossible de charger les Totems destinataires.',
            };
        }
    }
}
